import { useEffect, useMemo, useState } from 'react';
import type { ApiError } from '../../../lib/http/ApiError';
import { useFetch } from '../../../hooks/useFetch';
import { RightsProfilesApiFactory } from '../api/rights-profiles.api';
import type { CatalogueLine } from '../types/rights-profile.types';

interface UseRightsProfilesCatalogueResult {
  catalogue: CatalogueLine[] | null;
  loading: boolean;
  error: ApiError | Error | null;
}

/**
 * Fetches the permission catalogue (`GET /rights-profiles/catalogue`) that drives
 * the matrix screen: lines, levels, parents and applicable actions. The screen
 * reads the catalogue, it never recopies it (backend is the single source of
 * truth). Aborts the in-flight request on unmount.
 */
export function useRightsProfilesCatalogue(): UseRightsProfilesCatalogueResult {
  const [client, loading] = useFetch();
  const api = useMemo(() => RightsProfilesApiFactory(client), [client]);

  const [catalogue, setCatalogue] = useState<CatalogueLine[] | null>(null);
  const [error, setError] = useState<ApiError | Error | null>(null);

  useEffect(() => {
    const ctrl = new AbortController();
    setError(null);

    api
      .getCatalogue({ signal: ctrl.signal })
      .then(loaded => setCatalogue(loaded))
      .catch(err => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setCatalogue(null);
        setError(err instanceof Error ? err : new Error(String(err)));
      });

    return () => ctrl.abort();
  }, [api]);

  return { catalogue, loading, error };
}
